<template>
    <v-card class="investment-calculator elevation-2">
        <v-card-title class="primary--text pb-2">
            <div class="d-flex align-center justify-space-between w-100">
                <div class="d-flex align-center">
                    <v-icon left color="primary" size="28">mdi-calculator-variant</v-icon>
                    <div>
                        <div class="text-h5 font-weight-bold">Kalkulátor investície (VZOR pre investorov)</div>
                        <div class="text-caption grey--text">Naplánuj si svoju investíciu a pozri návratnosť</div>
                    </div>
                </div>
            </div>
        </v-card-title>

        <v-card-text class="pb-2">
            <!-- Quick stats -->
            <v-row dense class="mb-4">
                <v-col v-for="stat in quickStats" :key="stat.label" cols="6" md="3">
                    <v-card flat :color="stat.color + ' lighten-5'" class="pa-3 text-center stat-card" height="80">
                        <div class="text-caption text-truncate">{{ stat.label }}</div>
                        <div :class="stat.color + '--text'" class="text-h6 font-weight-bold text-truncate">
                            {{ stat.value }}
                        </div>
                    </v-card>
                </v-col>
            </v-row>

            <v-row>
                <!-- Ľavý panel - Nastavenia -->
                <v-col cols="12" lg="7">
                    <v-card outlined class="pa-4">
                        <div class="d-flex align-center mb-4">
                            <v-icon left color="primary">mdi-tune</v-icon>
                            <span class="text-subtitle-1 font-weight-medium">Parametre investície</span>
                        </div>

                        <!-- Suma investície s real-time feedback -->
                        <div class="mb-4">
                            <v-text-field v-model.number="investmentAmount" label="Suma investície" type="number"
                                step="10" min="10" max="1000000" suffix="€" outlined dense hide-details="auto"
                                :rules="investmentRules" :error="hasInvestmentError" :success="isValidInvestment"
                                @input="calculateResults" @focus="$event.target.select()" @keyup.enter="quickInvest">
                                <template v-slot:prepend-inner>
                                    <v-icon :color="investmentFieldColor">mdi-currency-eur</v-icon>
                                </template>
                                <template v-slot:append-outer>
                                    <v-menu offset-y>
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-btn icon small v-bind="attrs" v-on="on">
                                                <v-icon small color="primary">mdi-lightning-bolt</v-icon>
                                            </v-btn>
                                        </template>
                                        <v-list dense>
                                            <v-list-item v-for="preset in investmentPresets" :key="preset.value"
                                                @click="setInvestmentAmount(preset.value)">
                                                <v-list-item-content>
                                                    <v-list-item-title>{{ formatCurrency(preset.value)
                                                    }}</v-list-item-title>
                                                    <v-list-item-subtitle>{{ preset.label }}</v-list-item-subtitle>
                                                </v-list-item-content>
                                            </v-list-item>
                                        </v-list>
                                    </v-menu>
                                </template>
                            </v-text-field>

                            <!-- Live validation feedback -->
                            <v-expand-transition>
                                <div v-if="investmentAmount > 0" class="mt-2">
                                    <v-alert v-if="hasInvestmentError" dense outlined type="error" class="mb-0">
                                        {{ investmentErrorMessage }}
                                    </v-alert>
                                    <v-alert v-else-if="isValidInvestment" dense outlined type="success" class="mb-0">
                                        <v-icon left small>mdi-check</v-icon>
                                        Investícia je v poriadku!
                                    </v-alert>
                                </div>
                            </v-expand-transition>
                        </div>

                        <!-- Aktuálny koeficient displej -->
                        <v-card outlined :color="coefficientCard.color" class="pa-4 mb-4">
                            <div class="d-flex align-center justify-space-between">
                                <div>
                                    <div class="text-overline">Koeficient násobku vkladu</div>
                                    <div class="text-h3 font-weight-bold" :class="coefficientCard.textColor">
                                        {{ displayCoefficient }}
                                    </div>
                                    <div class="text-caption">
                                        <span v-if="availableAtCurrentCoefficient > 0">
                                            Ešte môžeš investovať: <strong>{{
                                                formatCurrency(availableAtCurrentCoefficient) }}</strong>
                                        </span>
                                        <span v-else class="warning--text">Koeficient bude znížený</span>
                                    </div>
                                </div>
                                <div class="text-right">
                                    <div class="text-overline">Moja účasť na investícií celkom</div>
                                    <div class="text-h5 success--text font-weight-bold">
                                        {{ formatCurrency(payoutClaim) }}
                                    </div>
                                    <div class="text-caption">
                                        Investícia + benefit
                                    </div>
                                </div>
                            </div>

                            <!-- Progress bar pre koeficient -->
                            <v-expand-transition>
                                <div v-if="availableAtCurrentCoefficient > 0" class="mt-3">
                                    <div class="d-flex align-center justify-space-between mb-1">
                                        <span class="text-caption">Využitie koeficientu {{ getCurrentSimpleCoefficient
                                        }}x</span>
                                        <span class="text-caption">{{ coefficientUtilization.toFixed(1) }}%</span>
                                    </div>
                                    <v-progress-linear :value="coefficientUtilization" height="6" rounded
                                        :color="coefficientProgressColor"
                                        background-color="grey lighten-3"></v-progress-linear>
                                </div>
                            </v-expand-transition>

                            <!-- Multi-contract breakdown -->
                            <v-expand-transition>
                                <div v-if="multipleContracts" class="mt-3">
                                    <v-divider class="mb-3"></v-divider>
                                    <div class="text-caption mb-2">
                                        <v-icon left small color="info">mdi-file-multiple</v-icon>
                                        Rozdelenie na {{ investmentBreakdown.length }} zmluvy:
                                    </div>
                                    <div v-for="(part, index) in investmentBreakdown" :key="index"
                                        class="d-flex justify-space-between align-center mb-1">
                                        <span class="text-caption">
                                            {{ formatCurrency(part.amount) }} × {{ part.coefficient }}x
                                        </span>
                                        <strong class="success--text text-caption">
                                            {{ formatCurrency(part.payout) }}
                                        </strong>
                                    </div>
                                </div>
                            </v-expand-transition>
                        </v-card>

                        <!-- Advanced settings -->
                        <v-expansion-panels flat>
                            <v-expansion-panel>
                                <v-expansion-panel-header class="pa-0">
                                    <div class="d-flex align-center">
                                        <v-icon left small>mdi-cog</v-icon>
                                        <span class="text-body-2">Pokročilé nastavenia</span>
                                    </div>
                                </v-expansion-panel-header>
                                <v-expansion-panel-content class="px-0">
                                    <v-row dense>
                                        <v-col cols="12" md="6">
                                            <v-text-field v-model.number="annualRevenue"
                                                label="Modelácia predpokladanej tržby ročne" type="number" suffix="€"
                                                outlined dense hide-details @input="debounceCalculate"
                                                @focus="$event.target.select()">
                                                <template v-slot:append-outer>
                                                    <v-btn icon small @click="resetToDefault">
                                                        <v-icon small>mdi-refresh</v-icon>
                                                    </v-btn>
                                                </template>
                                            </v-text-field>
                                        </v-col>
                                        <v-col cols="12" md="6">
                                            <div class="d-flex align-center">
                                                <v-slider v-model="payoutPercentage" min="60" max="70" step="1"
                                                    hide-details dense @input="calculateResults">
                                                    <template v-slot:prepend>
                                                        <span class="text-caption">{{ payoutPercentage }}%</span>
                                                    </template>
                                                </v-slider>
                                            </div>
                                            <div class="text-caption grey--text">Koeficient platby z tržieb v %</div>
                                        </v-col>
                                    </v-row>
                                </v-expansion-panel-content>
                            </v-expansion-panel>
                        </v-expansion-panels>
                    </v-card>
                </v-col>

                <!-- Pravý panel - Výsledky -->
                <v-col cols="12" lg="5">
                    <v-card outlined class="pa-4">
                        <div class="d-flex align-center mb-4">
                            <v-icon left color="success">mdi-chart-line</v-icon>
                            <span class="text-subtitle-1 font-weight-medium">Výsledky investície</span>
                        </div>

                        <!-- ROI Hero Section -->
                        <v-card flat color="success lighten-5" class="pa-4 mb-4 text-center">
                            <div class="text-overline">Ročná návratnosť (ROI)</div>
                            <div :class="roiClass" class="text-h3 font-weight-bold">
                                {{ roiPercentage }}%
                            </div>
                            <div class="text-caption">
                                Návrat za: <strong>{{ paybackPeriod }}</strong>
                            </div>
                        </v-card>

                        <!-- Key metrics -->
                        <v-row dense class="mb-3">
                            <v-col cols="6">
                                <div class="text-center">
                                    <div class="text-caption">Suma výplat za rok</div>
                                    <div class="text-h6 info--text font-weight-bold">
                                        {{ formatCurrency(myAnnualPayout) }}
                                    </div>
                                </div>
                            </v-col>
                            <v-col cols="6">
                                <div class="text-center">
                                    <div class="text-caption">Moja účasť %</div>
                                    <div class="text-h6 primary--text font-weight-bold">
                                        {{ myParticipationPercentage }}%
                                    </div>
                                </div>
                            </v-col>
                        </v-row>

                        <!-- Balance indicators -->
                        <div class="mb-3">
                            <div class="d-flex justify-space-between align-center mb-1">
                                <span class="text-caption">Stav po 1 roku (len investícia)</span>
                                <span :class="yearlyBalanceClass" class="text-caption font-weight-bold">
                                    {{ formatCurrency(Math.abs(yearlyBalance)) }}
                                    {{ yearlyBalance >= 0 ? 'zisk' : 'chýba' }}
                                </span>
                            </div>
                            <v-progress-linear
                                :value="Math.min(100, Math.max(0, (myAnnualPayout / investmentAmount) * 100))"
                                height="6" rounded
                                :color="yearlyBalance >= 0 ? 'success' : 'warning'"></v-progress-linear>
                        </div>

                        <div class="mb-4">
                            <div class="d-flex justify-space-between align-center mb-1">
                                <span class="text-caption">Stav po 1 roku (s benefitom)</span>
                                <span :class="yearlyBalanceWithBenefitClass" class="text-caption font-weight-bold">
                                    {{ formatCurrency(Math.abs(yearlyBalanceWithBenefit)) }}
                                    {{ yearlyBalanceWithBenefit >= 0 ? 'zisk' : 'chýba' }}
                                </span>
                            </div>
                            <v-progress-linear :value="Math.min(100, Math.max(0, (myAnnualPayout / payoutClaim) * 100))"
                                height="6" rounded
                                :color="yearlyBalanceWithBenefit >= 0 ? 'success' : 'info'"></v-progress-linear>
                        </div>

                        <!-- Summary table -->
                        <v-simple-table dense>
                            <template v-slot:default>
                                <thead>
                                    <tr>
                                        <th class="text-left text-caption">Popis</th>
                                        <th class="text-right text-caption">Suma</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td class="text-caption">Celková investícia všetkých</td>
                                        <td class="text-right text-caption font-weight-medium">{{
                                            formatCurrency(totalInvestmentsIncludingMine) }}</td>
                                    </tr>
                                    <tr>
                                        <td class="text-caption">Skutočné + tvoja projekcia</td>
                                        <td class="text-right text-caption font-weight-medium">{{
                                            formatCurrency(totalInvestmentsIncludingMine) }}</td>
                                    </tr>
                                    <tr>
                                        <td class="text-caption">Maximum plánovaných investícií</td>
                                        <td class="text-right text-caption font-weight-medium">{{
                                            formatCurrency(maxInvestmentAmount) }}</td>
                                    </tr>
                                    <tr>
                                        <td class="text-caption">Celkovo treba vrátiť (max)</td>
                                        <td class="text-right text-caption font-weight-medium">{{
                                            formatCurrency(23000000) }}</td>
                                    </tr>
                                </tbody>
                            </template>
                        </v-simple-table>
                    </v-card>
                </v-col>
            </v-row>

            <!-- Action buttons -->
            <v-row class="mt-4">
                <v-col cols="12" class="text-center">
                    <v-btn x-large color="success" :disabled="!isValidInvestment" :loading="loading" @click="invest"
                        class="mr-3 px-8" elevation="2">
                        <v-icon left>mdi-trending-up</v-icon>
                        Idem investovať {{ formatCurrency(investmentAmount) }}
                    </v-btn>

                    <v-btn x-large color="primary" outlined @click="showContract" :disabled="!isValidInvestment"
                        class="px-6">
                        <v-icon left>mdi-file-document</v-icon>
                        Náhľad mojej budúcej zmluvy
                    </v-btn>
                </v-col>
            </v-row>

            <!-- Informácie -->
            <v-row class="mt-6">
                <v-col cols="12">
                    <v-expansion-panels flat>
                        <v-expansion-panel>
                            <v-expansion-panel-header>
                                <div class="d-flex align-center">
                                    <v-icon left color="warning">mdi-information</v-icon>
                                    <span class="font-weight-medium">Dôležité informácie</span>
                                </div>
                            </v-expansion-panel-header>
                            <v-expansion-panel-content>
                                <v-row>
                                    <v-col cols="12" md="6">
                                        <div class="text-subtitle-2 mb-2">📋 Pravidlá investície:</div>
                                        <ul class="text-body-2 ml-4">
                                            <li>Minimálna investícia: <strong>10€</strong></li>
                                            <li>Len násobky <strong>10€</strong></li>
                                            <li>Maximum: <strong>1,000,000€</strong></li>
                                            <li>Číslo zmluvy: <strong>ID/Poradové</strong></li>
                                        </ul>
                                    </v-col>
                                    <v-col cols="12" md="6">
                                        <div class="text-subtitle-2 mb-2">⚡ Proces aktivácie:</div>
                                        <ul class="text-body-2 ml-4">
                                            <li>Platnosť po <strong>pripísaní na účet</strong></li>
                                            <li>Spracovanie: <strong>až 1 deň</strong></li>
                                            <li>Údaje sú <strong>informatívne</strong></li>
                                            <li>Možnosť <strong>zastavenia príjmu</strong> investícií</li>
                                        </ul>
                                    </v-col>
                                </v-row>
                            </v-expansion-panel-content>
                        </v-expansion-panel>
                    </v-expansion-panels>
                </v-col>
            </v-row>
        </v-card-text>
    </v-card>
</template>

<script>
import { debounce } from 'lodash';

export default {
    name: "InvestmentCalculator",

    props: {
        defaultAnnualRevenue: { type: Number, default: 3420000 }, // Z Excel analýzy
        totalInvestments: { type: Number, default: 2687420 }, // Z Excel analýzy
        maxInvestmentAmount: { type: Number, default: 10000000 }
    },

    data() {
        return {
            debounceCalculate: 0,
            investmentAmount: 1000,
            annualRevenue: this.defaultAnnualRevenue,
            payoutPercentage: 60, // Prednastavené 60%, možnosť 60-70%
            loading: false,

            // Koeficienty podľa Excel analýzy a komentárov
            coefficientLevels: [
                { threshold: 1000000, coefficient: 5 },
                { threshold: 2000000, coefficient: 4 },
                { threshold: 3000000, coefficient: 3 },
                { threshold: 4000000, coefficient: 2 },
                { threshold: 10000000, coefficient: 1.5 }
            ],

            investmentPresets: [
                { value: 100, label: 'Začiatočník' },
                { value: 500, label: 'Základná' },
                { value: 1000, label: 'Odporúčaná' },
                { value: 5000, label: 'Pokročilá' },
                { value: 10000, label: 'Prémiová' },
                { value: 50000, label: 'Veľká investícia' }
            ]
        };
    },

    computed: {
        // Validation
        investmentRules() {
            return [
                v => !!v || 'Suma je povinná',
                v => v >= 10 || 'Minimálna investícia je 10€',
                v => v % 10 === 0 || 'Len násobky 10€',
                v => v <= 1000000 || 'Maximum 1,000,000€'
            ];
        },

        hasInvestmentError() {
            return this.investmentAmount > 0 && !this.isValidInvestment;
        },

        investmentErrorMessage() {
            if (this.investmentAmount < 10) return 'Minimálna investícia je 10€';
            if (this.investmentAmount % 10 !== 0) return 'Investovať môžete len násobky 10€';
            if (this.investmentAmount > 1000000) return 'Maximálna investícia je 1,000,000€';
            return '';
        },

        isValidInvestment() {
            return this.investmentAmount >= 10 &&
                this.investmentAmount % 10 === 0 &&
                this.investmentAmount <= 1000000;
        },

        investmentFieldColor() {
            if (!this.investmentAmount) return 'grey';
            return this.isValidInvestment ? 'success' : 'error';
        },

        // Core calculations
        totalInvestmentsIncludingMine() {
            return this.totalInvestments + this.investmentAmount;
        },

        // Výpočet rozdelenia investície cez úrovne (podľa komentárov v Excel)
        investmentBreakdown() {
            return this.calculateInvestmentBreakdown();
        },

        revenueForPayouts() {
            return this.annualRevenue * (this.payoutPercentage / 100);
        },

        payoutClaim() {
            return this.investmentBreakdown.reduce((sum, b) => sum + b.payout, 0);
        },

        myAnnualPayout() {
            return this.revenueForPayouts * (this.investmentAmount / this.totalInvestmentsIncludingMine);
        },

        // Display helpers
        displayCoefficient() {
            const breakdown = this.investmentBreakdown;
            if (breakdown.length === 1) {
                return breakdown[0].coefficient + 'x';
            }
            return 'Mix';
        },

        getCurrentSimpleCoefficient() {
            const currentTotal = this.totalInvestments;
            for (let level of this.coefficientLevels) {
                if (currentTotal < level.threshold) {
                    return level.coefficient;
                }
            }
            return 1.5;
        },

        multipleContracts() {
            return this.investmentBreakdown.length > 1;
        },

        // Availability
        availableAtCurrentCoefficient() {
            const currentTotal = this.totalInvestments;
            for (let level of this.coefficientLevels) {
                if (currentTotal < level.threshold) {
                    return level.threshold - currentTotal;
                }
            }
            return 0;
        },

        coefficientUtilization() {
            const currentCoefficient = this.getCurrentSimpleCoefficient;
            const currentLevelThreshold = this.coefficientLevels.find(l => l.coefficient === currentCoefficient)?.threshold || 0;
            const previousThreshold = this.coefficientLevels.find(l => l.threshold < currentLevelThreshold)?.threshold || 0;
            const levelSize = currentLevelThreshold - previousThreshold;
            const usedInLevel = this.totalInvestments - previousThreshold;
            return Math.max(0, Math.min(100, (usedInLevel / levelSize) * 100));
        },

        // Returns and performance
        yearlyBalance() {
            return this.myAnnualPayout - this.investmentAmount;
        },

        yearlyBalanceWithBenefit() {
            return this.myAnnualPayout - this.payoutClaim;
        },

        roiPercentage() {
            if (this.investmentAmount === 0) return '0.0';
            return ((this.myAnnualPayout / this.investmentAmount) * 100).toFixed(2);
        },

        paybackPeriod() {
            if (this.myAnnualPayout <= 0) return 'Nikdy';
            const years = this.investmentAmount / this.myAnnualPayout;
            if (years > 10) return '10+ rokov';
            if (years > 1) return `${years.toFixed(1)} rokov`;
            const months = years * 12;
            return `${Math.ceil(months)} mesiacov`;
        },

        myParticipationPercentage() {
            return ((this.investmentAmount / this.totalInvestmentsIncludingMine) * 100).toFixed(4);
        },

        // UI State
        // statusChip() {
        //     if (!this.isValidInvestment && this.investmentAmount > 0) {
        //         return {
        //             color: 'error',
        //             textColor: 'white',
        //             icon: 'mdi-alert',
        //             text: 'Neplatná suma'
        //         };
        //     }
        //     if (this.availableAtCurrentCoefficient === 0) {
        //         return {
        //             color: 'warning',
        //             textColor: 'white',
        //             icon: 'mdi-alert-circle',
        //             text: 'Koeficient bude znížený'
        //         };
        //     }
        //     if (this.multipleContracts) {
        //         return {
        //             color: 'info',
        //             textColor: 'white',
        //             icon: 'mdi-file-multiple',
        //             text: 'Viacero zmlúv'
        //         };
        //     }
        //     return {
        //         color: 'success',
        //         textColor: 'white',
        //         icon: 'mdi-check',
        //         text: 'Pripravené na investíciu'
        //     };
        // },

        quickStats() {
            return [
                {
                    label: 'Nárok na vrátenie',
                    value: this.formatCurrency(this.payoutClaim),
                    color: 'success'
                },
                {
                    label: 'ROI ročne',
                    value: this.roiPercentage + '%',
                    color: this.roiClass.includes('success') ? 'success' :
                        this.roiClass.includes('warning') ? 'warning' : 'error'
                },
                {
                    label: 'Návrat za',
                    value: this.paybackPeriod,
                    color: 'info'
                },
                {
                    label: 'Koeficient',
                    value: this.displayCoefficient,
                    color: 'primary'
                }
            ];
        },

        coefficientCard() {
            const coeff = this.getCurrentSimpleCoefficient;
            const colors = {
                5: { color: 'success lighten-5', textColor: 'success--text' },
                4: { color: 'warning lighten-5', textColor: 'warning--text' },
                3: { color: 'orange lighten-5', textColor: 'orange--text' },
                2: { color: 'error lighten-5', textColor: 'error--text' },
                1.5: { color: 'grey lighten-3', textColor: 'grey--text text--darken-2' }
            };
            return colors[coeff] || colors[1.5];
        },

        coefficientProgressColor() {
            const coeff = this.getCurrentSimpleCoefficient;
            if (coeff >= 5) return 'success';
            if (coeff >= 4) return 'warning';
            if (coeff >= 3) return 'orange';
            return 'error';
        },

        yearlyBalanceClass() {
            return this.yearlyBalance >= 0 ? 'success--text' : 'error--text';
        },

        yearlyBalanceWithBenefitClass() {
            return this.yearlyBalanceWithBenefit >= 0 ? 'success--text' : 'error--text';
        },

        roiClass() {
            const roi = parseFloat(this.roiPercentage);
            if (roi >= 50) return 'success--text';
            if (roi >= 20) return 'warning--text';
            return 'error--text';
        }
    },

    watch: {
        defaultAnnualRevenue(newVal) {
            this.annualRevenue = newVal;
            this.calculateResults();
        }
    },

    mounted() {
        this.calculateResults();
        // Setup debounced calculation
        this.debounceCalculate = debounce(this.calculateResults, 300);
    },

    methods: {
        // Kľúčová metóda pre výpočet rozdelenia investície cez úrovne
        calculateInvestmentBreakdown() {
            const amount = this.investmentAmount;
            const currentTotal = this.totalInvestments;
            let breakdown = [];
            let remainingAmount = amount;
            let processedTotal = currentTotal;

            for (let level of this.coefficientLevels) {
                if (remainingAmount <= 0) break;

                const availableAtLevel = Math.max(0, level.threshold - processedTotal);
                if (availableAtLevel > 0) {
                    const amountAtLevel = Math.min(remainingAmount, availableAtLevel);

                    breakdown.push({
                        amount: amountAtLevel,
                        coefficient: level.coefficient,
                        payout: amountAtLevel * level.coefficient,
                        levelThreshold: level.threshold
                    });

                    remainingAmount -= amountAtLevel;
                    processedTotal += amountAtLevel;
                }
            }

            return breakdown;
        },

        calculateResults() {
            // Trigger reactive calculations
            this.$forceUpdate();
            this.$emit('annual-revenue-change', this.annualRevenue);
        },

        setInvestmentAmount(amount) {
            this.investmentAmount = amount;
            this.calculateResults();
        },

        resetToDefault() {
            this.annualRevenue = this.defaultAnnualRevenue;
            this.calculateResults();
        },

        quickInvest() {
            if (this.isValidInvestment) {
                this.invest();
            }
        },

        invest() {
            if (!this.isValidInvestment) return;

            this.loading = true;

            // Simulate API call delay
            setTimeout(() => {
                this.$emit('invest', this.investmentAmount);
                this.loading = false;
            }, 500);
        },

        showContract() {
            this.$emit('show-contract-preview', {
                amount: this.investmentAmount,
                coefficient: this.displayCoefficient,
                breakdown: this.investmentBreakdown,
                payoutClaim: this.payoutClaim
            });
        },

        formatCurrency(amount) {
            // Handle null, undefined, empty string
            if (amount == null || amount === '') return '0€';

            // Convert string to number (handles Django DecimalField)
            const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;

            // Check if conversion resulted in a valid number
            if (isNaN(numAmount)) return '0€';

            return new Intl.NumberFormat('sk-SK', {
                style: 'currency',
                currency: 'EUR'
            }).format(numAmount);
        }
    }
};
</script>

<style scoped>
/* Status chip styling */
.status-chip {
    font-size: 14px !important;
    height: 36px !important;
    min-width: 200px;
    border-radius: 6px !important;
}

.status-chip .v-icon {
    font-size: 18px !important;
}

.investment-calculator {
    margin-bottom: 24px;
    transition: all 0.3s ease;
}

.stat-card {
    transition: all 0.2s ease;
    cursor: default;
}

.stat-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.v-text-field>>>input {
    font-weight: 500;
    font-size: 1.1rem;
}

.v-btn--x-large {
    height: 56px !important;
    font-size: 16px !important;
    font-weight: 600 !important;
    border-radius: 8px !important;
}

/* Responsive improvements */
@media (max-width: 960px) {
    .v-btn--x-large {
        height: 48px !important;
        font-size: 14px !important;
    }

    .text-h3 {
        font-size: 1.8rem !important;
    }

    .text-h5 {
        font-size: 1.3rem !important;
    }
}

@media (max-width: 600px) {
    .v-card-title {
        padding: 16px 16px 8px 16px;
    }

    .v-card-text {
        padding: 8px 16px 16px 16px;
    }

    .stat-card {
        height: 70px !important;
    }

    .text-h6 {
        font-size: 1rem !important;
    }
}
</style>